import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from 'helpers/api';

export const documentRequestsApi = createApi({
  reducerPath: 'documentRequestsApi',
  baseQuery: createBaseQuery('document_requests'),
  tagTypes: ['DocumentRequests'],
  endpoints: builder => ({
    // Admin endpoints
    getDocumentRequests: builder.query({
      query: ({ checkId }) => `/candidate_check/${checkId}`,
      transformResponse: ({ result }) => result,
      providesTags: (result, error, { checkId }) => [{ type: 'DocumentRequests', id: checkId }],
    }),
    getCandidateDocumentRequests: builder.query({
      query: candidateId => `/candidate/${candidateId}`,
      transformResponse: ({ result }) => result,
      providesTags: (result, error, candidateId) => [
        { type: 'DocumentRequests', id: `candidate-${candidateId}` },
      ],
    }),
    createDocumentRequest: builder.mutation({
      query: ({ checkId, data }) => ({
        url: `/candidate_check/${checkId}`,
        method: 'POST',
        body: data,
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: (result, error, { checkId }) => [
        { type: 'DocumentRequests', id: checkId },
        { type: 'DocumentRequests', id: `candidate-${result?.candidate_id}` },
      ],
    }),
    getDocumentRequest: builder.query({
      query: ({ id }) => `/${id}`,
      transformResponse: ({ result }) => result,
    }),
    updateDocumentRequest: builder.mutation({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: result =>
        result ? [{ type: 'DocumentRequests', id: result.candidate_check_id }] : [],
    }),
    completeDocumentRequest: builder.mutation({
      query: ({ id }) => ({
        url: `/${id}/complete`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: result =>
        result
          ? [
              { type: 'DocumentRequests', id: result.candidate_check_id },
              { type: 'DocumentRequests', id: `candidate-${result.candidate_id}` },
            ]
          : [],
    }),
    resendDocumentRequest: builder.mutation({
      query: ({ id }) => ({
        url: `/${id}/resend`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: result =>
        result ? [{ type: 'DocumentRequests', id: result.candidate_check_id }] : [],
    }),
    notifyClientDocumentRequest: builder.mutation({
      query: ({ id }) => ({
        url: `/${id}/notify_client`,
        method: 'POST',
      }),
      transformResponse: ({ result }) => result,
      invalidatesTags: result =>
        result?.document_request
          ? [{ type: 'DocumentRequests', id: result.document_request.candidate_check_id }]
          : [],
    }),

    // Candidate-facing endpoints (token-based)
    handleDocumentUpload: builder.mutation({
      query: ({ token, upload_id }) => ({
        url: `/token/${token}/upload`,
        method: 'POST',
        body: { upload_id },
      }),
      transformResponse: ({ result }) => result,
    }),
    getDocumentRequestStatus: builder.query({
      query: ({ token, document_request_id }) =>
        `/token/${token}/status?document_request_id=${document_request_id}`,
      transformResponse: ({ result }) => result,
    }),
  }),
});

export const {
  // Admin hooks
  useGetDocumentRequestsQuery,
  useGetCandidateDocumentRequestsQuery,
  useLazyGetDocumentRequestsQuery,
  useCreateDocumentRequestMutation,
  useGetDocumentRequestQuery,
  useUpdateDocumentRequestMutation,
  useCompleteDocumentRequestMutation,
  useResendDocumentRequestMutation,
  useNotifyClientDocumentRequestMutation,
  // Candidate hooks
  useHandleDocumentUploadMutation,
  useLazyGetDocumentRequestStatusQuery,
} = documentRequestsApi;
